/**
 * Parabolic SAR — porte fiel do engine Silverlight
 * (GraficoDePrata/Formulas/FMLParabolicSAR.cs, classe FML.SAR).
 *
 * Três parâmetros, como no engine:
 *   ACCEL (accelerationFactor)    — AF inicial E valor de reset a cada virada
 *   STEP  (accelerationStep)      — incremento do AF quando o EP melhora
 *   MAXP  (maxAccelerationFactor) — teto do AF
 *
 * ⚠️ O que mudou em relação à implementação anterior do odin (2026-07-22):
 *
 * 1. **Aceleração.** A anterior somava o AF em TODA barra da mesma direção
 *    (`af = min(max, af + accel)`). Wilder — e o engine — só aceleram quando o
 *    **EP melhora** (`if (ep < price_high && …) start += STEP`). Era a causa
 *    dominante da divergência: 6,35% de concordância com o engine (WINFUT).
 * 2. **STEP desacoplado** do AF inicial: antes um único número fazia os dois
 *    papéis.
 * 3. **Clamp do teto.** O engine só incrementa se `start + STEP <= MAXP` (para
 *    ANTES de passar); a anterior clampava NO teto (`min(max, …)`). Com os
 *    defaults (0.02/0.02/0.2) dá no mesmo; com MAXP que não seja múltiplo,
 *    diverge.
 *
 * O engine varre o array INVERTIDO (índice 0 = mais recente). Aqui o porte é
 * cronológico com o mapeamento `i = n−1−t`, então `High[i+1]` (a barra anterior
 * no tempo) vira `high[t−1]`.
 *
 * Antes da barra-semente o engine devolve 0 (o `SarBuffer` nasce zerado e essas
 * posições nunca são escritas). Na borda com o chart isso vira `undefined` —
 * plotar 0 num gráfico de preço seria uma linha no rodapé.
 *
 * Paridade provada em __tests__/sar.silverlight.test.js.
 */
import { SAR as defaultOptions } from "./defaultOptionsForComputation";

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const ACCEL = options.accelerationFactor;
    const MAXP = options.maxAccelerationFactor;
    // `accelerationStep` ausente (config antigo / template legado) ⇒ Wilder
    // clássico, em que o incremento é o próprio AF inicial. Preserva o default
    // histórico. (Nome NÃO é `step`: o `NumericPropertyType` já usa essa chave
    // para o incremento do input numérico.)
    const STEP = options.accelerationStep ?? ACCEL;

    const n = data.length;
    const out = new Array(n).fill(undefined);
    if (n < 3) return out;

    const high = data.map((d) => d.high);
    const low = data.map((d) => d.low);

    // ---- busca da direção inicial (engine: i de Bars-2 até 1)
    let dirlong = true;
    let start = ACCEL;
    let lastHigh = -10000000.0;
    let lastLow = 10000000.0;
    let t = 1;
    while (t <= n - 2) {
      const priceLow = low[t];
      if (lastLow > priceLow) lastLow = priceLow;
      const priceHigh = high[t];
      if (lastHigh < priceHigh) lastHigh = priceHigh;
      if (priceHigh > high[t - 1] && priceLow > low[t - 1]) break;
      if (priceHigh < high[t - 1] && priceLow < low[t - 1]) {
        dirlong = false;
        break;
      }
      t++;
    }

    const sarBuf = new Array(n).fill(0);
    let ep;
    if (dirlong) {
      sarBuf[t] = low[t - 1];
      ep = high[t];
    } else {
      sarBuf[t] = high[t - 1];
      ep = low[t];
    }
    const semente = t;
    t++;

    let sar = 0;
    while (t <= n - 1) {
      const priceLow = low[t];
      const priceHigh = high[t];

      // ---- virada por rompimento do SAR anterior
      if (dirlong && priceLow < sarBuf[t - 1]) {
        start = ACCEL;
        dirlong = false;
        ep = priceLow;
        lastLow = priceLow;
        sarBuf[t] = lastHigh;
        t++;
        continue;
      }
      if (!dirlong && priceHigh > sarBuf[t - 1]) {
        start = ACCEL;
        dirlong = true;
        ep = priceHigh;
        lastHigh = priceHigh;
        sarBuf[t] = lastLow;
        t++;
        continue;
      }

      let price = sarBuf[t - 1];
      sar = price + start * (ep - price);

      if (dirlong) {
        // Só acelera quando o EP melhora — a regra de Wilder.
        if (ep < priceHigh && start + STEP <= MAXP) start += STEP;
        // Guarda do engine para a 1ª barra do loop; inalcançável na prática
        // (a semente nunca cai em t=0), portada por fidelidade.
        if (priceHigh < high[t - 1] && t === 1) sar = sarBuf[t - 1];

        price = low[t - 1];
        if (sar > price) sar = price;
        price = low[t - 2];
        if (sar > price) sar = price;

        if (sar > priceLow) {
          start = ACCEL;
          dirlong = false;
          ep = priceLow;
          lastLow = priceLow;
          sarBuf[t] = lastHigh;
          t++;
          continue;
        }
        if (ep < priceHigh) {
          lastHigh = priceHigh;
          ep = priceHigh;
        }
      } else {
        if (ep > priceLow && start + STEP <= MAXP) start += STEP;
        if (priceLow < low[t - 1] && t === 1) sar = sarBuf[t - 1];

        price = high[t - 1];
        if (sar < price) sar = price;
        price = high[t - 2];
        if (sar < price) sar = price;

        if (sar < priceHigh) {
          start = ACCEL;
          dirlong = true;
          ep = priceHigh;
          lastHigh = priceHigh;
          sarBuf[t] = lastLow;
          t++;
          continue;
        }
        if (ep > priceLow) {
          lastLow = priceLow;
          ep = priceLow;
        }
      }

      sarBuf[t] = sar;
      t++;
    }

    for (let k = semente; k < n; k++) out[k] = sarBuf[k];
    return out;
  }

  calculator.undefinedLength = function () {
    return 1;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
